import { useEffect, useMemo, useState } from "react";
import type { Identifier } from "ra-core";
import { useDataProvider, useGetList, useGetMany, useTranslate } from "ra-core";
import { Link } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

import {
  resolveThresholdHours,
  useAlertRulesById,
} from "../alerts/useLeadAlerts";
import { buildAlertMessage } from "../alerts/alertMessage";
import {
  groupAlertsByRule,
  groupStuckByStage,
  type DireccionAlertRow,
  type RuleAlertGroup,
} from "../alerts/groupAlerts";
import { findDealLabel } from "../deals/dealUtils";
import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type { AlertRuleType, Deal, LeadAlert } from "../types";

/**
 * Almikia Epic ER (vision #12, TASK-008): "leads that need attention" for
 * direccion (every open `lead_alerts` row) and, RLS-scoped to her own brazo
 * (TASK-003), a lead_manager. Mounted from `DireccionPanel.tsx`, which grows
 * by only the mount lines. Reuses TASK-007's `buildAlertMessage` sentence
 * builder and this file's own `groupAlerts.ts` pure helpers (unit-tested
 * there). Visibility comes entirely from the caller's own RLS/FakeRest
 * emulation on `lead_alerts` -- this component never widens or narrows it,
 * and it never groups, counts, sorts or filters by advisor, nor shows an
 * owner's name (ASSUMPTION, ticket description: the lead page's own
 * "Asignado a" + reassign action already covers that).
 */

const MAX_OPEN_ALERTS = 1000;
const MAX_VISIBLE_PER_GROUP = 5;
const DETENIDO_EN_ETAPA_RULE_TYPE: AlertRuleType = "detenido_en_etapa";

type Translate = ReturnType<typeof useTranslate>;

/**
 * Desktop navigates to the lead show; mobile registers no `deals` resource
 * (`MobileAdmin`, `CRM.tsx`), so this falls back to the lead's first
 * contact show there, matching `LeadAlertCards.alertCardHref` /
 * `HoyPersonCard.cardHref`. `null` when there is no contact to fall back to
 * (no link rather than a broken one).
 */
function alertRowHref(
  row: DireccionAlertRow,
  isMobile: boolean,
): string | null {
  if (!isMobile) return `/deals/${row.dealId}/show`;
  return row.contactId != null ? `/contacts/${row.contactId}/show` : null;
}

/**
 * Reads every open `lead_alerts` row the caller's RLS lets her see (one read,
 * acceptance criterion 5/NFR performance), joins it with its rule and deal,
 * and turns it into render-ready rows via `groupAlerts.ts`'s pure helpers.
 */
function useDireccionAlerts(): {
  groups: RuleAlertGroup[];
  stuckStages: ReturnType<typeof groupStuckByStage>;
  isPending: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { dealStages } = useConfigurationContext();
  const rulesById = useAlertRulesById();
  const dataProvider = useDataProvider<CrmDataProvider>();

  // Acceptance criterion 5 (refresh the TASK-005 engine on load) and the
  // NFR "one read of open alerts after at most one refresh call": the
  // `lead_alerts` read below only turns on once the evaluation settles, so
  // this section never fires the initial fetch and then a second one via
  // `refetch()` -- exactly one `useGetList` request per panel load.
  const [isEvaluated, setIsEvaluated] = useState(false);
  useEffect(() => {
    let isMounted = true;
    const evaluate = async () => {
      try {
        await dataProvider.evaluateLeadAlerts();
      } catch {
        // Non-blocking (same convention as
        // `useAdvisorLeadAlerts.evaluateAndRefresh`): a throttled/failed
        // evaluation just means this section reads whatever `lead_alerts`
        // already has.
      } finally {
        if (isMounted) setIsEvaluated(true);
      }
    };
    void evaluate();
    return () => {
      isMounted = false;
    };
  }, [dataProvider]);

  const {
    data: alerts,
    isPending: isAlertsPending,
    error: alertsError,
    refetch: refetchAlerts,
  } = useGetList<LeadAlert>(
    "lead_alerts",
    {
      pagination: { page: 1, perPage: MAX_OPEN_ALERTS },
      sort: { field: "raised_at", order: "ASC" },
      filter: { "resolved_at@is": null },
    },
    { enabled: isEvaluated },
  );

  const dealIds = useMemo(
    () => Array.from(new Set((alerts ?? []).map((alert) => alert.deal_id))),
    [alerts],
  );
  const {
    data: deals,
    isPending: isDealsPending,
    error: dealsError,
  } = useGetMany<Deal>(
    "deals",
    { ids: dealIds },
    { enabled: dealIds.length > 0 },
  );
  const dealsById = useMemo(
    () => new Map((deals ?? []).map((deal) => [deal.id, deal])),
    [deals],
  );

  const rows = useMemo(() => {
    const now = new Date();
    const result: DireccionAlertRow[] = [];
    for (const alert of alerts ?? []) {
      const rule = rulesById.get(alert.rule_id);
      const deal = dealsById.get(alert.deal_id);
      if (!rule || !deal) continue;
      const thresholdHours = resolveThresholdHours(
        rule,
        deal.stage,
        dealStages,
      );
      const message = buildAlertMessage({
        ruleType: rule.rule_type,
        raisedAt: alert.raised_at,
        now,
        thresholdHours,
        currentStageLabel: findDealLabel(dealStages, deal.stage),
        targetStageLabel: rule.target_stage_key
          ? findDealLabel(dealStages, rule.target_stage_key)
          : undefined,
      });
      if (!message) continue;
      result.push({
        id: alert.id,
        dealId: deal.id,
        contactId: deal.contact_ids[0] ?? null,
        ruleId: rule.id,
        ruleType: rule.rule_type,
        ruleLabel: rule.label,
        stageKey: deal.stage,
        stageLabel: findDealLabel(dealStages, deal.stage) ?? deal.stage,
        leadName: deal.name,
        message,
        raisedAt: alert.raised_at,
        staleSinceMs:
          new Date(alert.raised_at).getTime() - (thresholdHours ?? 0) * 3600000,
      });
    }
    return result;
  }, [alerts, rulesById, dealsById, dealStages]);

  const groups = useMemo(() => groupAlertsByRule(rows), [rows]);
  const stuckThreshold = useMemo(() => {
    const rule = Array.from(rulesById.values()).find(
      (candidate) => candidate.rule_type === DETENIDO_EN_ETAPA_RULE_TYPE,
    );
    return rule?.stuck_lead_count_threshold ?? Infinity;
  }, [rulesById]);
  const stuckStages = useMemo(
    () => groupStuckByStage(rows, stuckThreshold),
    [rows, stuckThreshold],
  );

  return {
    groups,
    stuckStages,
    // `!isEvaluated` keeps the skeleton showing while the TASK-005 refresh
    // runs (the `lead_alerts` read below stays disabled until then).
    // `isDealsPending` mirrors `useAdvisorLeadAlerts`'s own convention: it
    // only reflects a real in-flight request once `useGetMany` is actually
    // enabled, otherwise a panel with zero open alerts would stay on the
    // skeleton forever.
    isPending:
      !isEvaluated || isAlertsPending || (dealIds.length > 0 && isDealsPending),
    error: alertsError ?? dealsError,
    refetch: refetchAlerts,
  };
}

const RuleAlertGroupSection = ({
  group,
  expanded,
  onExpand,
  translate,
}: {
  group: RuleAlertGroup;
  expanded: boolean;
  onExpand: () => void;
  translate: Translate;
}) => {
  const isMobile = useIsMobile();
  const shownAlerts = expanded
    ? group.alerts
    : group.alerts.slice(0, MAX_VISIBLE_PER_GROUP);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-base font-bold">{group.ruleLabel}</p>
        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {translate("crm.dashboard.direccion.alerts.rule_group_leads_count", {
            smart_count: group.alerts.length,
          })}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {shownAlerts.map((alert) => {
          const href = alertRowHref(alert, isMobile);
          const rowContent = (
            <>
              <span className="truncate text-base font-bold">
                {alert.leadName}
              </span>
              <span className="truncate text-base text-muted-foreground">
                {alert.stageLabel} · {alert.message}
              </span>
            </>
          );
          return href ? (
            <Link
              key={alert.id}
              to={href}
              className="flex min-h-11 flex-col justify-center gap-0.5 rounded-2xl bg-card p-3"
            >
              {rowContent}
            </Link>
          ) : (
            <div
              key={alert.id}
              className="flex min-h-11 flex-col justify-center gap-0.5 rounded-2xl bg-card p-3"
            >
              {rowContent}
            </div>
          );
        })}
        {!expanded && group.alerts.length > MAX_VISIBLE_PER_GROUP ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-11 self-start"
            onClick={onExpand}
          >
            {translate("crm.dashboard.direccion.alerts.view_all", {
              count: group.alerts.length,
            })}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export const DireccionAlertsSection = () => {
  const translate = useTranslate();
  const { groups, stuckStages, isPending, error, refetch } =
    useDireccionAlerts();
  const [expandedRuleIds, setExpandedRuleIds] = useState<Set<Identifier>>(
    () => new Set(),
  );

  const isEmpty = groups.length === 0;

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-xl font-semibold text-muted-foreground">
        {translate("crm.dashboard.direccion.alerts.section_title")}
      </h2>

      {error ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={refetch}
        />
      ) : isPending ? (
        <ListRowsSkeleton rows={4} columns={2} />
      ) : isEmpty ? (
        <ListEmptyState
          title={translate("crm.dashboard.direccion.alerts.empty_title")}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {stuckStages.length > 0 ? (
            <div className="flex flex-col gap-2">
              {stuckStages.map((stage) =>
                stage.isHighlighted ? (
                  <Alert key={stage.stageKey} variant="destructive">
                    <AlertDescription>
                      {translate(
                        "crm.dashboard.direccion.alerts.stuck_stage_highlight",
                        { smart_count: stage.count, stage: stage.stageLabel },
                      )}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <p
                    key={stage.stageKey}
                    className="text-base text-muted-foreground"
                  >
                    {translate(
                      "crm.dashboard.direccion.alerts.stuck_stage_summary",
                      { smart_count: stage.count, stage: stage.stageLabel },
                    )}
                  </p>
                ),
              )}
            </div>
          ) : null}

          {groups.map((group) => (
            <RuleAlertGroupSection
              key={group.ruleId}
              group={group}
              expanded={expandedRuleIds.has(group.ruleId)}
              onExpand={() =>
                setExpandedRuleIds((previous) => {
                  const next = new Set(previous);
                  next.add(group.ruleId);
                  return next;
                })
              }
              translate={translate}
            />
          ))}
        </div>
      )}
    </section>
  );
};
